import TopNavBar from "app/layout/TopNavBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SubscriptionService from "app/services/SubscriptionServices";

const PACKAGE_IDS = ["1-Trial", "100-family", "200-basic", "300-lite", "400-pro"];
const PACKAGE_LABELS = ["Trial", "Family", "Basic", "Lite", "Pro"];
const RECENT_PAGE_SIZE = 10;

function SubscriptionDashboard() {
    const cellStyle = { border: "1px solid #ddd", padding: "8px", fontSize: "0.85em" };
    const thStyle = { ...cellStyle, fontWeight: "bold", background: "#f5f5f5" };

    const [allSubscriptions, setAllSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trialFilter, setTrialFilter] = useState("all");       // "all" | "exclude" | "only"
    const [trendView, setTrendView] = useState("monthly");        // "monthly" | "weekly"
    const [recentPage, setRecentPage] = useState(1);
    const [recentPackageFilter, setRecentPackageFilter] = useState(""); // "" = all
    const subscriptionService = SubscriptionService();
    const navigate = useNavigate();

    const loadData = () => {
        setLoading(true);
        subscriptionService.getAllSubscriptions({ page: 1, pageSize: 5000 })
            .then(r => { setAllSubscriptions(r?.data || []); setLoading(false); })
            .catch(() => navigate("/admin/subscribers/list"));
    };

    useEffect(() => { loadData(); }, []);

    const handleTrialFilter = (val) => {
        setTrialFilter(val);
        setRecentPage(1);
        setRecentPackageFilter("");
    };

    const handleRecentPackageFilter = (val) => {
        setRecentPackageFilter(val);
        setRecentPage(1);
    };

    const metrics = useMemo(() => {
        if (!allSubscriptions.length) return null;

        const filtered = allSubscriptions.filter(s => {
            if (trialFilter === "exclude") return s.package_id !== "1-Trial";
            if (trialFilter === "only") return s.package_id === "1-Trial";
            return true;
        });

        if (!filtered.length) return { empty: true };

        const today = new Date();
        const in30 = new Date(today);
        in30.setDate(today.getDate() + 30);

        const totalActive = filtered.filter(s => s.is_active).length;
        const totalRevenue = filtered.reduce((sum, s) => sum + (Number(s.price) || 0), 0);

        const newThisMonth = filtered.filter(s => {
            if (!s.start_date) return false;
            const d = new Date(s.start_date);
            return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
        }).length;

        const expiringList = filtered
            .filter(s => {
                if (!s.end_date) return false;
                const d = new Date(s.end_date);
                return d >= today && d <= in30;
            })
            .sort((a, b) => new Date(a.end_date) - new Date(b.end_date));

        const lowBalanceList = filtered
            .filter(s => s.package_id !== "1-Trial" && s.unites_per_package > 0 && (s.balance / s.unites_per_package) < 0.20)
            .sort((a, b) => (a.balance / a.unites_per_package) - (b.balance / b.unites_per_package));

        const revenueByPackage = PACKAGE_IDS.map(pid =>
            filtered.filter(s => s.package_id === pid)
                .reduce((sum, s) => sum + (Number(s.price) || 0), 0)
        );
        const countByPackage = PACKAGE_IDS.map(pid =>
            filtered.filter(s => s.package_id === pid).length
        );
        const avgBalancePctByPackage = PACKAGE_IDS.map(pid => {
            const group = filtered.filter(s => s.package_id === pid && s.unites_per_package > 0);
            if (!group.length) return 0;
            return group.reduce((sum, s) => sum + (s.balance / s.unites_per_package) * 100, 0) / group.length;
        });

        const byPackageRows = PACKAGE_IDS.map((pid, i) => ({
            id: pid,
            label: PACKAGE_LABELS[i],
            count: countByPackage[i],
            revenue: revenueByPackage[i],
            avgBalancePct: Math.round(avgBalancePctByPackage[i]),
        }));

        // Monthly trend — last 12 months
        const monthlyLabels = [];
        const monthlyData = [];
        for (let i = 11; i >= 0; i--) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            monthlyLabels.push(d.toLocaleString('default', { month: 'short', year: 'numeric' }));
            monthlyData.push(filtered.filter(s => {
                if (!s.start_date) return false;
                const sd = new Date(s.start_date);
                return sd.getFullYear() === d.getFullYear() && sd.getMonth() === d.getMonth();
            }).length);
        }

        // Weekly trend — last 16 weeks starting from most recent Monday
        const weeklyLabels = [];
        const weeklyData = [];
        const dayOfWeek = today.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const thisMonday = new Date(today);
        thisMonday.setDate(today.getDate() - daysToMonday);
        thisMonday.setHours(0, 0, 0, 0);

        for (let i = 15; i >= 0; i--) {
            const weekStart = new Date(thisMonday);
            weekStart.setDate(thisMonday.getDate() - i * 7);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 7);
            weeklyLabels.push(weekStart.toLocaleString('default', { month: 'short', day: 'numeric' }));
            weeklyData.push(filtered.filter(s => {
                if (!s.start_date) return false;
                const sd = new Date(s.start_date);
                return sd >= weekStart && sd < weekEnd;
            }).length);
        }

        // Full sorted list for Recent Subscriptions — pagination + package filter in render
        const recentList = [...filtered]
            .sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0));

        return {
            totalActive, totalRevenue, newThisMonth,
            expiringList, expiringCount: expiringList.length,
            lowBalanceList, lowBalanceCount: lowBalanceList.length,
            revenueByPackage, countByPackage, avgBalancePctByPackage,
            byPackageRows,
            monthlyLabels, monthlyData,
            weeklyLabels, weeklyData,
            recentList,
        };
    }, [allSubscriptions, trialFilter]);

    const toggleBtnStyle = (active) => ({
        padding: '4px 12px', fontSize: '0.8em', fontWeight: 500,
        background: active ? '#1976d2' : '#e0e0e0',
        color: active ? '#fff' : '#444',
        border: 'none', borderRadius: 4, cursor: 'pointer',
    });

    const filterBtnStyle = (active) => ({
        padding: '5px 14px',
        background: active ? '#1976d2' : '#e0e0e0',
        color: active ? '#fff' : '#333',
        border: 'none', borderRadius: 4, fontWeight: 500, cursor: 'pointer', fontSize: '0.85em',
    });

    const pageBtnStyle = (disabled) => ({
        padding: '4px 12px', fontWeight: 500,
        background: disabled ? '#e0e0e0' : '#1976d2',
        color: disabled ? '#aaa' : '#fff',
        border: 'none', borderRadius: 4,
        cursor: disabled ? 'default' : 'pointer',
    });

    if (loading) {
        return (
            <DashboardLayout>
                <div>
                    <TopNavBar />
                    <MDBox py={3}><MDTypography>Loading dashboard...</MDTypography></MDBox>
                </div>
            </DashboardLayout>
        );
    }

    // Recent list — apply local package filter then paginate
    const baseRecentList = metrics?.recentList || [];
    const filteredRecentList = recentPackageFilter
        ? baseRecentList.filter(s => s.package_id === recentPackageFilter)
        : baseRecentList;
    const recentTotalPages = Math.max(1, Math.ceil(filteredRecentList.length / RECENT_PAGE_SIZE));
    const recentSlice = filteredRecentList.slice(
        (recentPage - 1) * RECENT_PAGE_SIZE,
        recentPage * RECENT_PAGE_SIZE
    );

    const trendLabels = trendView === "weekly" ? metrics?.weeklyLabels : metrics?.monthlyLabels;
    const trendData = trendView === "weekly" ? metrics?.weeklyData : metrics?.monthlyData;

    return (
        <DashboardLayout>
            <div>
                <TopNavBar />

                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <h2 style={{ margin: 0 }}>Subscription Dashboard</h2>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <button type="button" onClick={loadData}
                            style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 500, cursor: 'pointer' }}>
                            &#x21bb; Refresh
                        </button>
                        <button type="button" onClick={() => navigate("/admin/subscribers/list")}
                            style={{ padding: '6px 16px', background: '#555', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 500, cursor: 'pointer' }}>
                            Subscribers List
                        </button>
                    </div>
                </div>

                {/* Global Trial filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20 }}>
                    <span style={{ fontSize: '0.85em', color: '#555', marginRight: 4 }}>Package filter:</span>
                    <button type="button" style={filterBtnStyle(trialFilter === "all")} onClick={() => handleTrialFilter("all")}>All</button>
                    <button type="button" style={filterBtnStyle(trialFilter === "exclude")} onClick={() => handleTrialFilter("exclude")}>Exclude Trial</button>
                    <button type="button" style={filterBtnStyle(trialFilter === "only")} onClick={() => handleTrialFilter("only")}>Trial Only</button>
                </div>

                {metrics && !metrics.empty && (
                    <>
                        {/* KPI Cards */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <MDBox mb={1.5}>
                                    <ComplexStatisticsCard color="info" icon="subscriptions"
                                        title="Active Subscriptions" count={metrics.totalActive}
                                        percentage={{ color: "success", amount: "", label: "currently active" }} />
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <MDBox mb={1.5}>
                                    <ComplexStatisticsCard color="success" icon="payments"
                                        title="Total Revenue" count={`Rs. ${metrics.totalRevenue.toLocaleString()}`}
                                        percentage={{ color: "text", amount: "", label: "all time" }} />
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <MDBox mb={1.5}>
                                    <ComplexStatisticsCard color="primary" icon="person_add"
                                        title="New This Month" count={metrics.newThisMonth}
                                        percentage={{ color: "success", amount: "", label: "subscriptions" }} />
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <MDBox mb={1.5}>
                                    <ComplexStatisticsCard color="warning" icon="event_busy"
                                        title="Expiring in 30 Days" count={metrics.expiringCount}
                                        percentage={{ color: "warning", amount: "", label: "subscriptions" }} />
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <MDBox mb={1.5}>
                                    <ComplexStatisticsCard color="error" icon="battery_alert"
                                        title="Low Balance" count={metrics.lowBalanceCount}
                                        percentage={{ color: "error", amount: "< 20%", label: "units remaining" }} />
                                </MDBox>
                            </Grid>
                        </Grid>

                        {/* Charts Row 1 */}
                        <MDBox mt={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <HorizontalBarChart
                                        icon={{ component: "bar_chart", color: "info" }}
                                        title="Revenue by Package"
                                        description="Total amount collected per package (Rs.)"
                                        height="15rem"
                                        chart={{
                                            labels: PACKAGE_LABELS,
                                            datasets: [{ label: "Revenue (Rs.)", color: "info", data: metrics.revenueByPackage }],
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <DefaultDoughnutChart
                                        icon={{ component: "donut_large", color: "warning" }}
                                        title="By Package"
                                        description="Subscription count per package"
                                        height="15rem"
                                        chart={{
                                            labels: PACKAGE_LABELS,
                                            datasets: {
                                                label: "Subscriptions",
                                                backgroundColors: ["info", "success", "warning", "error", "dark"],
                                                data: metrics.countByPackage,
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </MDBox>

                        {/* Charts Row 2 — Signup Trend with Monthly/Weekly toggle */}
                        <MDBox mt={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 4 }}>
                                            <button type="button" style={toggleBtnStyle(trendView === "monthly")} onClick={() => setTrendView("monthly")}>Monthly</button>
                                            <button type="button" style={toggleBtnStyle(trendView === "weekly")} onClick={() => setTrendView("weekly")}>Weekly</button>
                                        </div>
                                        <DefaultLineChart
                                            icon={{ component: "show_chart", color: "success" }}
                                            title="Signup Trend"
                                            description={trendView === "weekly" ? "New subscriptions per week (last 16 weeks)" : "New subscriptions per month (last 12 months)"}
                                            height="15rem"
                                            chart={{
                                                labels: trendLabels || [],
                                                datasets: [{ label: "New Subscriptions", color: "info", data: trendData || [] }],
                                            }}
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <VerticalBarChart
                                        icon={{ component: "account_balance_wallet", color: "dark" }}
                                        title="Avg Balance Remaining"
                                        description="Average % of units still available"
                                        height="15rem"
                                        chart={{
                                            labels: PACKAGE_LABELS,
                                            datasets: [{ label: "Avg. Balance %", color: "success", data: metrics.avgBalancePctByPackage.map(v => Math.round(v)) }],
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </MDBox>

                        {/* By Package Summary Table */}
                        <MDBox mt={3}>
                            <Card>
                                <MDBox pt={2} px={2} pb={1}>
                                    <MDTypography variant="h6" fontWeight="medium">By Package Summary</MDTypography>
                                </MDBox>
                                <MDBox px={2} pb={2}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Package</th>
                                                <th style={thStyle}>Count</th>
                                                <th style={thStyle}>Total Revenue (Rs.)</th>
                                                <th style={thStyle}>Avg Balance %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics.byPackageRows.map((row, idx) => (
                                                <tr key={row.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                    <td style={{ ...cellStyle, fontWeight: 500 }}>
                                                        {row.label} <span style={{ color: '#999', fontSize: '0.85em' }}>({row.id})</span>
                                                    </td>
                                                    <td style={cellStyle}>{row.count}</td>
                                                    <td style={cellStyle}>Rs. {row.revenue.toLocaleString()}</td>
                                                    <td style={{
                                                        ...cellStyle,
                                                        color: row.avgBalancePct < 20 ? '#d32f2f' : row.avgBalancePct > 60 ? '#2e7d32' : 'inherit',
                                                        fontWeight: row.avgBalancePct < 20 ? 'bold' : 'normal',
                                                    }}>
                                                        {row.avgBalancePct}%
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr style={{ background: '#e8f4fd' }}>
                                                <td style={{ ...cellStyle, fontWeight: 'bold' }}>Total</td>
                                                <td style={{ ...cellStyle, fontWeight: 'bold' }}>{metrics.byPackageRows.reduce((s, r) => s + r.count, 0)}</td>
                                                <td style={{ ...cellStyle, fontWeight: 'bold' }}>Rs. {metrics.byPackageRows.reduce((s, r) => s + r.revenue, 0).toLocaleString()}</td>
                                                <td style={cellStyle}>—</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </MDBox>
                            </Card>
                        </MDBox>

                        {/* Low Balance Table */}
                        <MDBox mt={3}>
                            <Card>
                                <MDBox pt={2} px={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="h6" fontWeight="medium">
                                        Low Balance — below 20% of package units
                                    </MDTypography>
                                    <span style={{
                                        background: metrics.lowBalanceCount > 0 ? '#d32f2f' : '#2e7d32',
                                        color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: '0.8em',
                                    }}>
                                        {metrics.lowBalanceCount}
                                    </span>
                                </MDBox>
                                <MDBox px={2} pb={2} style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Name</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Tel</th>
                                                <th style={thStyle}>Package</th>
                                                <th style={thStyle}>Balance</th>
                                                <th style={thStyle}>Units/Pkg</th>
                                                <th style={thStyle}>Balance %</th>
                                                <th style={thStyle}>End Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics.lowBalanceList.map((sub, idx) => {
                                                const pct = sub.unites_per_package > 0
                                                    ? Math.round((sub.balance / sub.unites_per_package) * 100)
                                                    : 0;
                                                return (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fff8f8' }}>
                                                        <td style={cellStyle}>{sub.first_name} {sub.last_name}</td>
                                                        <td style={{ ...cellStyle, fontSize: '0.78em', color: '#444' }}>{sub.user}</td>
                                                        <td style={cellStyle}>{sub.tel}</td>
                                                        <td style={cellStyle}>{sub.package_id}</td>
                                                        <td style={{ ...cellStyle, color: '#d32f2f', fontWeight: 'bold' }}>{sub.balance}</td>
                                                        <td style={cellStyle}>{sub.unites_per_package}</td>
                                                        <td style={{ ...cellStyle, color: '#d32f2f', fontWeight: 'bold' }}>{pct}%</td>
                                                        <td style={cellStyle}>{sub.end_date?.substring(0, 10)}</td>
                                                    </tr>
                                                );
                                            })}
                                            {metrics.lowBalanceList.length === 0 && (
                                                <tr>
                                                    <td colSpan={8} style={{ ...cellStyle, textAlign: 'center', color: '#888' }}>
                                                        No low balance subscriptions
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </MDBox>
                            </Card>
                        </MDBox>

                        {/* Expiring Soon */}
                        <MDBox mt={3}>
                            <Card>
                                <MDBox pt={2} px={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="h6" fontWeight="medium">
                                        Expiring Soon (next 30 days)
                                    </MDTypography>
                                    <span style={{
                                        background: metrics.expiringCount > 0 ? '#ed6c02' : '#2e7d32',
                                        color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: '0.8em',
                                    }}>
                                        {metrics.expiringCount}
                                    </span>
                                </MDBox>
                                <MDBox px={2} pb={2} style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Name</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Package</th>
                                                <th style={thStyle}>End Date</th>
                                                <th style={thStyle}>Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics.expiringList.slice(0, 10).map((sub, idx) => (
                                                <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                    <td style={cellStyle}>{sub.first_name} {sub.last_name}</td>
                                                    <td style={{ ...cellStyle, fontSize: '0.78em', color: '#444' }}>{sub.user}</td>
                                                    <td style={cellStyle}>{sub.package_id}</td>
                                                    <td style={cellStyle}>{sub.end_date?.substring(0, 10)}</td>
                                                    <td style={{
                                                        ...cellStyle,
                                                        color: sub.unites_per_package > 0 && (sub.balance / sub.unites_per_package) < 0.2 ? '#d32f2f' : 'inherit',
                                                        fontWeight: sub.unites_per_package > 0 && (sub.balance / sub.unites_per_package) < 0.2 ? 'bold' : 'normal',
                                                    }}>
                                                        {sub.balance}
                                                    </td>
                                                </tr>
                                            ))}
                                            {metrics.expiringList.length === 0 && (
                                                <tr>
                                                    <td colSpan={5} style={{ ...cellStyle, textAlign: 'center', color: '#888' }}>
                                                        None expiring in 30 days
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </MDBox>
                            </Card>
                        </MDBox>

                        {/* Recent Subscriptions */}
                        <MDBox mt={3} mb={4}>
                            <Card>
                                <MDBox pt={2} px={2} pb={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                    <MDTypography variant="h6" fontWeight="medium">Recent Subscriptions</MDTypography>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ fontSize: '0.8em', color: '#555' }}>Package:</span>
                                            <select
                                                value={recentPackageFilter}
                                                onChange={e => handleRecentPackageFilter(e.target.value)}
                                                style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.8em', background: '#fafafa' }}
                                            >
                                                <option value="">All</option>
                                                {PACKAGE_IDS.map((pid, i) => (
                                                    <option key={pid} value={pid}>{PACKAGE_LABELS[i]}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <span style={{ fontSize: '0.8em', color: '#777' }}>
                                            Page {recentPage} of {recentTotalPages} ({filteredRecentList.length} total)
                                        </span>
                                    </div>
                                </MDBox>
                                <MDBox px={2} pb={2} style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Name</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Tel</th>
                                                <th style={thStyle}>Address</th>
                                                <th style={thStyle}>Package</th>
                                                <th style={thStyle}>Start Date</th>
                                                <th style={thStyle}>Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentSlice.map((sub, idx) => (
                                                <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                    <td style={cellStyle}>{sub.first_name} {sub.last_name}</td>
                                                    <td style={{ ...cellStyle, fontSize: '0.78em', color: '#444' }}>{sub.user}</td>
                                                    <td style={cellStyle}>{sub.tel}</td>
                                                    <td style={{ ...cellStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sub.address}>{sub.address}</td>
                                                    <td style={cellStyle}>{sub.package_id}</td>
                                                    <td style={cellStyle}>{sub.start_date?.substring(0, 10)}</td>
                                                    <td style={cellStyle}>Rs. {Number(sub.price).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                            {recentSlice.length === 0 && (
                                                <tr>
                                                    <td colSpan={7} style={{ ...cellStyle, textAlign: 'center', color: '#888' }}>
                                                        No subscriptions match the selected package
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                        <button type="button"
                                            disabled={recentPage <= 1}
                                            onClick={() => setRecentPage(p => p - 1)}
                                            style={pageBtnStyle(recentPage <= 1)}>
                                            &#8592; Prev
                                        </button>
                                        <button type="button"
                                            disabled={recentPage >= recentTotalPages}
                                            onClick={() => setRecentPage(p => p + 1)}
                                            style={pageBtnStyle(recentPage >= recentTotalPages)}>
                                            Next &#8594;
                                        </button>
                                    </div>
                                </MDBox>
                            </Card>
                        </MDBox>
                    </>
                )}

                {metrics?.empty && (
                    <MDBox py={3}>
                        <MDTypography color="text">No subscriptions match the current filter.</MDTypography>
                    </MDBox>
                )}
                {!metrics && !loading && (
                    <MDBox py={3}>
                        <MDTypography color="text">No subscription data available.</MDTypography>
                    </MDBox>
                )}
            </div>
        </DashboardLayout>
    );
}

export default SubscriptionDashboard;
